/**
 * BNPL payment screen logic (ERS-78 screen 09)
 *
 * Consents, the "Zahlungsart" choice (`bnpl` vs `invoice`), the detail style, the provider submit
 * sequence, decline handling and the invoice opt-out. The personal data form lives in
 * `useBnplCheckoutScreen` (08); this hook reads what it persisted and sends a resumed session back
 * there when `privateInfo` is incomplete.
 *
 * References:
 * - `api/coursePaymentCallables.ts` — `startBnplCoursePayment`
 * - `hooks/useBnplLegalDocument.ts` — cached DATA_PRIVACY consent text
 * - `insurance/bnplBillingCustomer.ts` — `hasCompletePrivateInfo`, `buildBillingCustomerFromPatient`
 */

import type {
  Insurance,
  PatientTermsAcceptanceCheckbox,
  RecordPatientTermsAcceptanceRequest,
  RecordPatientTermsAcceptanceResponse,
} from '@physiofit-digital/physiofit_types';
import { PATIENT_TRACKING_EVENTS, type PatientTrackingEventPayload } from '@physiofit-digital/physiofit_types';
import { useFeatureFlagVariantKey, usePostHog } from '@posthog/react';
import { httpsCallable } from 'firebase/functions';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { functions } from '../../../firebaseConfig';
import { type OnboardingCoursePaymentError, startBnplCoursePayment } from '../api/coursePaymentCallables';
import { buildBillingCustomerFromPatient, hasCompletePrivateInfo } from '../insurance/bnplBillingCustomer';
import {
  BNPL_CHECKOUT_GENERIC_ERROR_NOTICE,
  BNPL_CHECKOUT_LIABILITY_CHECKBOX_TEXT,
  BNPL_CHECKOUT_PROVIDER_PRIVACY_CHECKBOX_TEXT,
  buildBnplPaymentTimeline,
} from '../insurance/bnplCopy';
import { isBnplProviderConsentCollectionEnabled, isSoftLaunchPayment } from '../insurance/bnplSoftLaunch';
import { buildProviderPrivacyTerm, PHYSIOFIT_PRIVACY_NOTICE_TERM } from '../insurance/consentTerms';
import { formatEuro } from '../insurance/copyUtils';
import { displayInsuranceName, displayInsuranceShortName } from '../insurance/displayInsuranceName';
import {
  isPatientSubmittingInsurer,
  resolveBillingTypeFromInsurance,
  resolveInsuranceCoverage,
} from '../insurance/metaBuilders';
import { getBnplCheckoutPath } from '../insurance/routing';
import { ONBOARDING_APP_VERSION_LABEL } from '../integrations/posthog/telemetry';
import { editSignedUpPatient } from '../services/editSignedUpPatient';
import { useSaveOnboardingStep } from '../services/onboardingProgress';
import { getSignedUpPatient, getSignUpId, useOnboardingStore } from '../store/onboardingStore';
import { useBnplLegalDocument } from './useBnplLegalDocument';
import { useInsuranceExplainBillingGuard } from './useInsuranceExplainBillingGuard';
import { usePatientAppLegal } from './usePatientAppLegal';
import { useSelectedInsurance } from './useSelectedInsurance';

export type BnplPaymentMethod = 'bnpl' | 'invoice';
export type BnplDetailStyle = 'drawer' | 'inline';

/** How long 09 waits for the store mirror of 08's write before treating `privateInfo` as incomplete. */
const PRIVATE_INFO_MIRROR_GRACE_MS = 1500;

/** Multivariate flag in both PostHog projects; variants `drawer` (control) and `inline`. */
export const BNPL_DETAIL_STYLE_FLAG_KEY = 'bnpl-detail-style';

/**
 * Detail style behind the "So funktioniert's" trigger. Non-production builds show the inline style
 * for BARMER so reviewers can compare both styles on the dev host; otherwise the PostHog variant
 * decides, with the drawer as the control and as the fallback until the flag has loaded.
 */
function resolveBnplDetailStyle(insurance: Insurance | null, variant: string | boolean | undefined): BnplDetailStyle {
  if (import.meta.env.MODE !== 'production' && insurance?.slug === 'barmer') {
    return 'inline';
  }
  return variant === 'inline' ? 'inline' : 'drawer';
}

/**
 * The consent document's `link` comes back from the provider, so it is validated before it can
 * reach an `href`. Anything that is not an http(s) URL degrades to the in-app drawer, which renders
 * the same consent text.
 */
function toExternalHttpUrl(value: string | undefined): string | undefined {
  const trimmed = value?.trim();
  if (!trimmed) {
    return undefined;
  }
  try {
    const { protocol } = new URL(trimmed);
    return protocol === 'https:' || protocol === 'http:' ? trimmed : undefined;
  } catch {
    return undefined;
  }
}

function buildLiabilityTerm(
  legal: ReturnType<typeof usePatientAppLegal>,
  acceptedDate: string,
): PatientTermsAcceptanceCheckbox {
  const fallbackTitle = 'Haftungsausschluss & Kontraindikatoren';
  return {
    type: 'liability',
    checkboxText: BNPL_CHECKOUT_LIABILITY_CHECKBOX_TEXT,
    title: 'Haftungsausschluss',
    infoTitle: legal.status === 'ready' ? legal.legal.liabilityTitle || fallbackTitle : fallbackTitle,
    infoText: legal.status === 'ready' ? legal.legal.liabilityText || '' : '',
    acceptedDate,
  };
}

export function useBnplPaymentScreen() {
  useSaveOnboardingStep('insurance_bnpl_payment');
  // Set before every `billingType` write (provider decline, invoice opt-out) and never cleared —
  // the only ways off this screen after either are the rejected screen or install.
  const exitHandledRef = useRef(false);
  useInsuranceExplainBillingGuard('bnpl', { suppressedRef: exitHandledRef });
  // Both survive a failed submit, which leaves the patient on this screen to press the CTA again.
  // The order event is the A/B experiment's primary metric, and the terms record is a legal record;
  // neither may be emitted twice for one acceptance. The terms ref keys on the method and the provider
  // consent text: switching to "Kauf auf Rechnung", or re-ticking a provider text that changed
  // (`consent_outdated`), is a genuinely different acceptance, not a retry.
  const orderCompletedCapturedRef = useRef(false);
  const recordedTermsKeyRef = useRef<string | null>(null);

  const navigate = useNavigate();
  const posthog = usePostHog();
  const signUpId = useOnboardingStore(getSignUpId);
  const signedUpPatient = useOnboardingStore(getSignedUpPatient);
  const { insurance, loading: insuranceLoading, error: insuranceError } = useSelectedInsurance();
  const legalResult = usePatientAppLegal();
  const bnplLegal = useBnplLegalDocument();

  // A resumed session (deep link, reload) whose `privateInfo` is not complete has no billing
  // customer to submit — send it back to 08 rather than let the submit fail on 09. Checked after a
  // grace period, not on mount: the document listener surfaces 08's write only with the next
  // snapshot, which arrives after this screen's first render, and an immediate check bounced every
  // first submit back to 08 (measured 2026-09-05, ERS-78).
  useEffect(() => {
    if (!signedUpPatient || hasCompletePrivateInfo(signedUpPatient)) {
      return;
    }
    const timer = window.setTimeout(
      () => navigate(getBnplCheckoutPath(), { replace: true }),
      PRIVATE_INFO_MIRROR_GRACE_MS,
    );
    return () => window.clearTimeout(timer);
  }, [signedUpPatient, navigate]);

  const coverage = useMemo(() => (insurance ? resolveInsuranceCoverage(insurance) : null), [insurance]);

  const insuranceName = useMemo(() => (insurance ? displayInsuranceName(insurance) : ''), [insurance]);
  const insuranceShortName = useMemo(
    () => (insurance ? displayInsuranceShortName(insurance) || insuranceName : insuranceName),
    [insurance, insuranceName],
  );

  const [paymentMethod, setPaymentMethod] = useState<BnplPaymentMethod>('bnpl');
  const [liabilityAccepted, setLiabilityAccepted] = useState(false);
  const [providerPrivacyAccepted, setProviderPrivacyAccepted] = useState(false);
  const [isProviderPrivacyDrawerOpen, setIsProviderPrivacyDrawerOpen] = useState(false);
  const [isDetailOpen, setIsDetailOpen] = useState(false);
  const [isDetailExpanded, setIsDetailExpanded] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [consentError, setConsentError] = useState(false);
  const [submitError, setSubmitError] = useState<OnboardingCoursePaymentError | null>(null);
  const consentSectionRef = useRef<HTMLDivElement>(null);

  // Same cache/override pattern as the former checkout hook: tracks the DATA_PRIVACY document
  // actually displayed right now, so a `consent_outdated` retry compares against what the patient
  // just re-confirmed, and the checkbox link/text update to match.
  const [consentOverride, setConsentOverride] = useState<{
    consentText: string;
    link?: string;
    displayType?: string;
  } | null>(null);
  const consentWasUpdated = consentOverride != null;
  const effectiveConsentDocument = consentOverride ?? (bnplLegal.status === 'ready' ? bnplLegal.document : null);
  const displayedConsentText = effectiveConsentDocument?.consentText ?? null;
  const consentLink =
    effectiveConsentDocument?.displayType === 'URL' ? toExternalHttpUrl(effectiveConsentDocument.link) : undefined;
  // `displayType: 'PLAIN_TEXT'` means `consentText` IS the document, so it gets a drawer. A document
  // with no `displayType` at all is the ERS-75 placeholder: an acknowledgement line, nothing to open.
  const inlineConsentDocument =
    effectiveConsentDocument?.displayType && effectiveConsentDocument.displayType !== 'URL'
      ? effectiveConsentDocument
      : null;

  const providerConsentEnabled = isBnplProviderConsentCollectionEnabled();
  // The RBI checkbox exists only for the BNPL method — switching to invoice removes it from the DOM.
  const providerConsentShown = paymentMethod === 'bnpl' && providerConsentEnabled;

  const allConsentsAccepted = liabilityAccepted && (!providerConsentShown || providerPrivacyAccepted);

  const legalDataReady = legalResult.status === 'ready';
  const bnplLegalUnavailable = providerConsentShown && bnplLegal.status === 'unavailable' && consentOverride == null;

  const forwardDisabled =
    insuranceLoading ||
    !legalDataReady ||
    (providerConsentShown && bnplLegal.status === 'loading') ||
    bnplLegalUnavailable ||
    isSubmitting;
  const forwardInactive = !allConsentsAccepted;

  const showConsentError = consentError && (forwardInactive || Boolean(submitError));

  const detailVariant = useFeatureFlagVariantKey(BNPL_DETAIL_STYLE_FLAG_KEY);
  const detailStyle = resolveBnplDetailStyle(insurance, detailVariant);
  const detailOpenedRef = useRef(false);
  const patientSubmits = insurance ? isPatientSubmittingInsurer(insurance) : false;
  const timeline = useMemo(
    () =>
      coverage ? buildBnplPaymentTimeline(insuranceShortName, coverage, patientSubmits) : [],
    [insuranceShortName, coverage, patientSubmits],
  );
  const ctaAmount = formatEuro(0, 'plain');

  const handleSelectPaymentMethod = useCallback((value: string) => {
    const method = value === 'invoice' ? 'invoice' : 'bnpl';
    setPaymentMethod(method);
    if (method === 'invoice') {
      setProviderPrivacyAccepted(false);
    }
  }, []);

  const handleOpenDetail = useCallback(() => {
    detailOpenedRef.current = true;
    const props: PatientTrackingEventPayload<'web_insurance_bnpl_payment_details'> = {
      insuranceId: insurance?.id,
      billingType: 'bnpl',
      detailStyle,
    };
    posthog.capture(PATIENT_TRACKING_EVENTS.web_insurance_bnpl_payment_details, props);
    if (detailStyle === 'drawer') {
      setIsDetailOpen(true);
    } else {
      setIsDetailExpanded((current) => !current);
    }
  }, [posthog, insurance?.id, detailStyle]);

  const handleBack = useCallback(() => {
    navigate(getBnplCheckoutPath());
  }, [navigate]);

  const revertToInsurerDefault = useCallback(async () => {
    if (!insurance) {
      return;
    }
    const insurerBillingType = resolveBillingTypeFromInsurance(insurance);
    await editSignedUpPatient({
      // Field path, NOT a spread of the local `insuranceInfo` snapshot — `recordPatientTermsAcceptance`
      // below writes `webAppOnboarding.checkoutInfo` back onto this same document, and re-sending a
      // stale copy of the whole map would clobber whatever landed in between.
      'webAppOnboarding.insuranceInfo.billingType': insurerBillingType,
      // Without this, the cohort condition still holds — a patient returning to insurance-select and
      // re-picking their insurer would be funnelled straight back into the flow they were just
      // declined from. `isInBnplBetaCohort` reads this flag first.
      'webAppOnboarding.flags.bnplDeclinedDate': new Date().toISOString(),
    });
  }, [insurance]);

  const handleForward = useCallback(async () => {
    if (forwardInactive) {
      setConsentError(true);
      consentSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }
    if (!signUpId || !signedUpPatient?.course?.id || !insurance || !coverage) return;
    if (paymentMethod === 'bnpl' && providerConsentEnabled && !displayedConsentText) return;
    // `buildBillingCustomerFromPatient` fills missing fields with '', so an incomplete resumed
    // session would post a blank customer to the provider. The mirror-grace redirect below is a
    // fallback, not a gate: its timer restarts on every snapshot of this document.
    if (!hasCompletePrivateInfo(signedUpPatient)) {
      navigate(getBnplCheckoutPath(), { replace: true });
      return;
    }

    // The primary A/B metric: the order was placed, whatever the provider answers afterwards.
    if (!orderCompletedCapturedRef.current) {
      orderCompletedCapturedRef.current = true;
      const orderProps: PatientTrackingEventPayload<'web_insurance_bnpl_order_completed'> = {
        billingType: 'bnpl',
        paymentMethod,
        detailStyle,
        detailOpened: detailOpenedRef.current,
      };
      posthog.capture(PATIENT_TRACKING_EVENTS.web_insurance_bnpl_order_completed, orderProps);
    }

    const submitStartedAt = Date.now();
    setIsSubmitting(true);
    setSubmitError(null);
    setConsentError(false);
    const acceptedDate = new Date().toISOString();
    const privateInfo = signedUpPatient.webAppOnboarding?.privateInfo;
    const personalInfo = {
      firstName: privateInfo?.firstName ?? '',
      lastName: privateInfo?.lastName ?? '',
      phoneNumber: signedUpPatient.patientPhone ?? '',
    };
    const common = {
      userVariant: 'withTherapist' as const,
      signedUpPatientId: signedUpPatient.id,
      courseZppName: signedUpPatient.course?.name,
      deviceInfo: {
        platform: typeof navigator !== 'undefined' ? navigator.platform : 'unknown',
        systemVersion: typeof navigator !== 'undefined' ? navigator.userAgent : 'unknown',
        appVersion: ONBOARDING_APP_VERSION_LABEL,
      },
    };

    try {
      const privacyNoticeTerm = { ...PHYSIOFIT_PRIVACY_NOTICE_TERM, acceptedDate };
      const liabilityTerm = buildLiabilityTerm(legalResult, acceptedDate);
      const recordPatientTermsAcceptance = httpsCallable<
        RecordPatientTermsAcceptanceRequest,
        RecordPatientTermsAcceptanceResponse
      >(functions, 'recordPatientTermsAcceptance');

      if (paymentMethod === 'invoice') {
        // Record first: a failed callable then leaves the document on `bnpl`, and the retry starts clean.
        if (recordedTermsKeyRef.current !== 'invoice') {
          await recordPatientTermsAcceptance({
            ...common,
            personalInfo,
            billingType: 'invoice',
            termsData: [privacyNoticeTerm, liabilityTerm],
          });
          recordedTermsKeyRef.current = 'invoice';
        }
        // The guard on this screen watches billingType; the write below flips it, so suppress first
        // (ERS-5 race, see `useInsuranceExplainBillingGuard`).
        exitHandledRef.current = true;
        await editSignedUpPatient({ 'webAppOnboarding.insuranceInfo.billingType': 'invoice' });
        const invoiceProps: PatientTrackingEventPayload<'web_insurance_invoice_checkout_completed'> = {
          billingType: 'invoice',
          optOutFromBnpl: true,
        };
        // `$set` corrects the person property stamped at insurance-select, so person-level
        // breakdowns by billingType no longer count the opt-out as bnpl.
        posthog.capture(PATIENT_TRACKING_EVENTS.web_insurance_invoice_checkout_completed, {
          ...invoiceProps,
          $set: { billingType: 'invoice' },
        });
        navigate('/webAppOnboarding/install');
        return;
      }

      const providerConsentText = providerConsentEnabled ? (displayedConsentText as string) : '';
      const bnplTermsKey = `bnpl:${providerConsentText}`;
      const bnplTermsData = providerConsentEnabled
        ? [
            privacyNoticeTerm,
            buildProviderPrivacyTerm({
              checkboxText: BNPL_CHECKOUT_PROVIDER_PRIVACY_CHECKBOX_TEXT,
              consentText: providerConsentText,
              link: consentLink,
              acceptedDate,
            }),
            liabilityTerm,
          ]
        : [privacyNoticeTerm, liabilityTerm];

      // Run in parallel: `startBnplCoursePayment` does not read the terms-acceptance record, so
      // there is no data dependency between the two.
      const [, result] = await Promise.all([
        recordedTermsKeyRef.current !== bnplTermsKey
          ? recordPatientTermsAcceptance({
              ...common,
              personalInfo,
              billingType: 'bnpl',
              termsData: bnplTermsData,
            }).then(() => {
              recordedTermsKeyRef.current = bnplTermsKey;
            })
          : Promise.resolve(),
        startBnplCoursePayment({
          signedUpPatientId: signedUpPatient.id,
          courseId: signedUpPatient.course.id,
          billingCustomer: buildBillingCustomerFromPatient(signedUpPatient),
          displayedConsentText: providerConsentText,
        }),
      ]);

      if (!result.ok) {
        setSubmitError(result.error);
        setIsSubmitting(false);
        return;
      }

      if (result.data.outcome === 'consent_outdated') {
        const { consentText, link, displayType } = result.data.legalDocument;
        setConsentOverride({ consentText, link, displayType });
        setProviderPrivacyAccepted(false);
        setSubmitError(null);
        setIsSubmitting(false);
        return;
      }

      if (result.data.outcome === 'declined') {
        // A decline is silent for the patient (ERS-81): it asks nothing of them and costs them
        // nothing — the course simply bills through the insurer's default flow after completion
        // instead of the provider's — so they get the same success path as an authorization. The
        // decline is visible to us only, through the event below and the console line.
        //
        // None of these three is rendered: they are analytics/support only. `declineStep` is the
        // only one always present:
        // `reasonCode` accompanies a perform-authorization decline, `failReason` only a body that
        // states one, so a breakdown has to group by the step (ERS-41).
        posthog.capture(PATIENT_TRACKING_EVENTS.web_insurance_bnpl_checkout_rejected, {
          billingType: 'bnpl',
          declineStep: result.data.declineStep,
          reasonCode: result.data.reasonCode,
          failReason: result.data.failReason,
          durationMs: Date.now() - submitStartedAt,
        });
        // The screen shows nothing, so this is the only trace in an open devtools while working on
        // the flow. The durable record is the event above, not this line.
        console.error('[BNPL] provider declined the course payment', {
          declineStep: result.data.declineStep,
          reasonCode: result.data.reasonCode,
          failReason: result.data.failReason,
        });
        // Before the revert, not after: `revertToInsurerDefault` writes `billingType` away from
        // 'bnpl', and Firestore's local write pushes that into the store through the live listener
        // while this `await` is still pending. Unsuppressed, the explain guard re-runs on that store
        // change with this screen still mounted and `replace`s to the fallback insurer's process
        // overview.
        exitHandledRef.current = true;
        await revertToInsurerDefault();
        // Deliberately NOT `webAppOnboarding.state: 'awaiting_rbi'` as the authorized branch below
        // does: that state means a provider order is pending. Leaving it unset, together with the
        // `billingType` the revert just restored, is what routes the finished course into the
        // fallback submission instead.
        //
        // `replace`: this checkout is spent either way, and leaving it on the stack means Back
        // re-enters the flow the provider just declined.
        navigate('/webAppOnboarding/install', { replace: true });
        return;
      }

      // outcome === 'authorized'
      // `softLaunch` separates a mocked acceptance from a real one for good: the person property set at
      // insurance-select says `bnpl` for both, and nothing else records which path the server took.
      posthog.capture(PATIENT_TRACKING_EVENTS.web_insurance_bnpl_checkout_completed, {
        billingType: 'bnpl',
        durationMs: Date.now() - submitStartedAt,
        softLaunch: isSoftLaunchPayment(result.data.rbiCoursePaymentId),
      });
      await editSignedUpPatient({ 'webAppOnboarding.state': 'awaiting_rbi' });
      navigate('/webAppOnboarding/install');
    } catch (error) {
      // Never sign the patient out and never navigate away — a failure must cost one tap, not a
      // new SMS (the PF-5683 lesson).
      setSubmitError({
        kind: 'unknown',
        message: error instanceof Error ? error.message : BNPL_CHECKOUT_GENERIC_ERROR_NOTICE,
      });
      setIsSubmitting(false);
    }
  }, [
    forwardInactive,
    signUpId,
    signedUpPatient,
    insurance,
    coverage,
    paymentMethod,
    providerConsentEnabled,
    displayedConsentText,
    consentLink,
    detailStyle,
    legalResult,
    posthog,
    revertToInsurerDefault,
    navigate,
  ]);

  return {
    insurance,
    insuranceLoading,
    insuranceError,
    legalResult,
    bnplLegal,
    coverage,
    insuranceName,
    insuranceShortName,
    providerConsentEnabled,
    providerConsentShown,
    consentLink,
    inlineConsentDocument,
    isProviderPrivacyDrawerOpen,
    setIsProviderPrivacyDrawerOpen,
    consentWasUpdated,
    effectiveConsentDocument,
    paymentMethod,
    handleSelectPaymentMethod,
    liabilityAccepted,
    setLiabilityAccepted,
    providerPrivacyAccepted,
    setProviderPrivacyAccepted,
    isDetailOpen,
    setIsDetailOpen,
    isDetailExpanded,
    consentError: showConsentError,
    consentSectionRef,
    isSubmitting,
    submitError,
    forwardDisabled,
    forwardInactive,
    detailStyle,
    timeline,
    patientSubmits,
    ctaAmount,
    handleBack,
    handleForward,
    handleOpenDetail,
  };
}

export type UseBnplPaymentScreenResult = ReturnType<typeof useBnplPaymentScreen>;
